import {
    TextField,
    InputAdornment,
    List,
    ListItem,
    Divider,
    IconButton,
    Box,
    Typography,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Popover,
    Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import styles from '../../styles/siteList.module.scss';
import { closeViewDialog, fetchPrimaryListData, openViewDialog, setFilteredCostCenterList, setFilteredSiteList } from 'modules/cbre-flow-view-management/ducks/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { DIALOG_SUBTYPES, DIALOG_TYPES } from 'modules/cbre-flow-view-management/ducks/constants';
import { getSiteList, getCurrentView, getCurrentViewFilteredCostCenterList, getCurrentViewFilteredSiteList, getNoAccessSiteList, getViewManagementListApiUrl, getViewsInfo } from 'modules/cbre-flow-view-management/ducks/selectors';
import { ArrowBack } from '@mui/icons-material';
import { requestAccessAction } from 'modules/cbre-flow-site-location/ducks/actions';
import { getSiteInfoUrl, RequestAccessUrl } from 'modules/cbre-flow-site-location/ducks/selectors';
import apiService from 'util/apiService';

interface ListItemData {
    siteId: number;
    siteName: string;
    site: string;
    client: string;
    clientCode: string;
    sitequicklinks: any[];
    accessLevel: string;
    accessStatus: string;
}

interface ViewManagementSiteListProps {
    selectedClient: any;
}

interface RemovedFilterInfo {
    id: string;
    label: string;
    type: 'Cost Center';
    siteName: string;
}

const getSiteSelectionState = (items: any[]) => new Set(items.map((item) => item.site));

const areSetsEqual = (left: Set<string>, right: Set<string>) => {
    if (left.size !== right.size) {
        return false;
    }

    for (const value of left) {
        if (!right.has(value)) {
            return false;
        }
    }

    return true;
};

const normalizeSiteSelectionForComparison = (selection: Set<string>, allAccessSites: Set<string>) => {
    return selection.size === 0 ? new Set(allAccessSites) : selection;
};

const getFilterSiteTokens = (item: any) => {
    return [item?.site, item?.siteName, item?.siteId]
        .filter((value) => value !== null && value !== undefined && value !== '')
        .map((value) => String(value));
};

const buildSelectedSiteLookup = (selectedSites: any[]) => {
    return new Set(
        selectedSites.flatMap((site) => [site?.site, site?.siteName, site?.siteId])
            .filter((value) => value !== null && value !== undefined && value !== '')
            .map((value) => String(value))
    );
};

const isFilterApplicableToSelection = (item: any, selectedLookup: Set<string>) => {
    const tokens = getFilterSiteTokens(item);

    if (!tokens.length) {
        return true;
    }

    return tokens.some((token) => selectedLookup.has(token));
};

const ViewManagementSiteList: React.FC<ViewManagementSiteListProps> = ({ selectedClient }) => {

    const listItems: ListItemData[] = useSelector(getSiteList)
    const sitesWithoutAccess: ListItemData[] = useSelector(getNoAccessSiteList)
    const filteredList: ListItemData[] = useSelector(getCurrentViewFilteredSiteList);
    const filteredCostCenters = useSelector(getCurrentViewFilteredCostCenterList);
    const requestAccessUrl = useSelector(RequestAccessUrl);
    const currentView = useSelector(getCurrentView);
    const currentViewInfo = useSelector(getViewsInfo);
    const [requestAccessLoading, setRequestAccessLoading] = useState<boolean>(false);
    const viewManagementListApiUrl = useSelector(getViewManagementListApiUrl);

    const dispatch = useDispatch();
    const [addedItems, setAddedItems] = useState<Set<string>>(new Set([])); // Initially some items are added
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(new Set(['withAccess', 'withoutAccess']));
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [searchResults, setSearchResults] = useState<{ withAccess: ListItemData[], withoutAccess: ListItemData[] }>({ withAccess: [], withoutAccess: [] });
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [requestAccessAnchorEl, setRequestAccessAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedSiteForRequest, setSelectedSiteForRequest] = useState<ListItemData | null>(null);
    const [popoverPosition, setPopoverPosition] = useState<'above' | 'below'>('below');
    const [removedFiltersPreview, setRemovedFiltersPreview] = useState<RemovedFilterInfo[]>([]);

    const searchUrl = `${viewManagementListApiUrl}&clientCode=${selectedClient?.clientCode}&searchFrom=SITE`;

    // Separate items by access
    const sitesWithAccess = listItems.filter(item => item.accessStatus === 'GRANTED' && item.accessLevel !== "NO_ACCESS");
    // const sitesWithoutAccess = listItems.filter(item => item.accessLevel !== "EDIT" && item.accessLevel !== "READ_ONLY");

    const siteUrl = useSelector(getSiteInfoUrl);

    const handleClose = () => {
        dispatch(closeViewDialog());
    };

    useEffect(() => {
        // Reset added items when component mounts
        console.log("testing filteredList", filteredList)
        setAddedItems(new Set(filteredList.filter((item: any) => item.accessLevel === 'EDIT' || item.accessLevel === 'READ_ONLY').map((item: any) => item.site)));
        setRemovedFiltersPreview([]);
    }, [filteredList]);

    useEffect(() => {
        // Debounce search
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm.trim()) {
                handleSearch(searchTerm);
            } else {
                setIsSearching(false);
                setSearchResults({ withAccess: [], withoutAccess: [] });
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handleSearch = async (keyword: string) => {
        setSearchLoading(true);
        setIsSearching(true);
        try {
            const response = await apiService.get(`${searchUrl}&search=${encodeURIComponent(keyword)}`);
            if (response.statusCode === 200) {
                const accessSites = response.data?.accessSites?.data || [];
                const noAccessSites = response.data?.noAccessSites?.data || [];

                const withAccess = accessSites.filter((item: ListItemData) => item.accessStatus === 'GRANTED' && item.accessLevel !== "NO_ACCESS");
                const withoutAccess = noAccessSites;

                setSearchResults({ withAccess, withoutAccess });
            } else {
                setSearchResults({ withAccess: [], withoutAccess: [] });
            }
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults({ withAccess: [], withoutAccess: [] });
        } finally {
            setSearchLoading(false);
        }
    };

    const displaySitesWithAccess = isSearching ? searchResults.withAccess : sitesWithAccess;
    const displaySitesWithoutAccess = isSearching ? searchResults.withoutAccess : sitesWithoutAccess;
    const allAccessSites = new Set(sitesWithAccess.map((item) => item.site));

    const initialSelectedSites = getSiteSelectionState(filteredList.filter((item: any) => item.accessLevel === 'EDIT' || item.accessLevel === 'READ_ONLY'));
    const hasPendingSiteChanges = !areSetsEqual(
        normalizeSiteSelectionForComparison(addedItems, allAccessSites),
        normalizeSiteSelectionForComparison(initialSelectedSites, allAccessSites)
    );

    const getAllSelectableSites = () => {
        const siteMap = new Map<string, any>();
        [...listItems, ...filteredList, ...searchResults.withAccess, ...displaySitesWithAccess].forEach((item) => {
            if (item?.site) {
                siteMap.set(item.site, item);
            }
        });

        return siteMap;
    };

    const getSelectedSitesFromSet = (selectedSitesSet: Set<string>) => {
        const selectableSites = getAllSelectableSites();
        return Array.from(selectedSitesSet)
            .map((siteKey) => selectableSites.get(siteKey))
            .filter(Boolean);
    };

    const getRemovedFiltersForSelection = (selectedSites: any[]) => {
        const selectedLookup = selectedSites.length
            ? buildSelectedSiteLookup(selectedSites)
            : buildSelectedSiteLookup(sitesWithAccess);

        const removedCostCenters = filteredCostCenters
            .filter((costCenter: any) => !isFilterApplicableToSelection(costCenter, selectedLookup))
            .map((costCenter: any) => ({
                id: `cost-center-${costCenter.costCenterId}`,
                label: costCenter.displayName || (costCenter.name && costCenter.description ? `${costCenter.name}-${costCenter.description}` : costCenter.name || costCenter.description) || 'Cost Center',
                type: 'Cost Center' as const,
                siteName: costCenter.siteName || costCenter.site || 'Selected site'
            }));

        return removedCostCenters;
    };

    const handleToggleItem = (site: string) => {
        const newAddedItems = new Set(addedItems);
        if (addedItems.has(site)) {
            newAddedItems.delete(site);
        } else {
            newAddedItems.add(site);
        }
        const selectedSites = getSelectedSitesFromSet(newAddedItems);
        const removedFilters = getRemovedFiltersForSelection(selectedSites);

        setAddedItems(newAddedItems);
        setRemovedFiltersPreview(removedFilters);
    };

    const handleUndoSiteSelection = () => {
        setAddedItems(new Set(initialSelectedSites));
        setRemovedFiltersPreview([]);
    };

    const warningExamples = Object.values(
        removedFiltersPreview.reduce((acc, item) => {
            if (!acc[item.siteName]) {
                acc[item.siteName] = { siteName: item.siteName, type: item.type, labels: [] };
            }
            acc[item.siteName].labels.push(item.label);
            return acc;
        }, {} as Record<string, { siteName: string; type: string; labels: string[] }>)
    );

    const handleRequestAccessClick = (event: React.MouseEvent<HTMLElement>, site: any) => {
        setSelectedSiteForRequest(site);
        const anchorElement = event.currentTarget;
        setRequestAccessAnchorEl(anchorElement);
        
        // Calculate if there's enough space below the button
        const rect = anchorElement.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const popoverHeight = 300; // Approximate popover height
        
        // Show above if not enough space below
        setPopoverPosition(spaceBelow < popoverHeight && spaceAbove > spaceBelow ? 'above' : 'below');
    };

    const handleRequestAccess = async () => {
        if (!selectedSiteForRequest) return;

        // Implement your request access logic here
        const requestActionPayload = {
            siteRequests: [{
                site: selectedSiteForRequest.site,
            }],
            requestReason: "",
            appCode: "ELIS",
            clientCode: (selectedClient as any).clientCode
        }
        setRequestAccessLoading(true);
        await dispatch(requestAccessAction({
            apiUrl: requestAccessUrl, data: requestActionPayload,
            isUpdateSiteInfoRequired: false
        }));
        const apiUrl = `${viewManagementListApiUrl}&clientCode=${selectedClient.clientCode}&searchFrom=SITE`;
        await dispatch(fetchPrimaryListData(apiUrl, 'noAccessSiteList'));
        setRequestAccessLoading(false);
        setRequestAccessAnchorEl(null);
        setSelectedSiteForRequest(null);
    }

    const handleCancelRequestAccess = () => {
        setRequestAccessAnchorEl(null);
        setSelectedSiteForRequest(null);
    };

    const handleAccordionToggle = (accordionId: string) => {
        const newExpanded = new Set(expandedAccordions);
        if (expandedAccordions.has(accordionId)) {
            newExpanded.delete(accordionId);
        } else {
            newExpanded.add(accordionId);
        }
        setExpandedAccordions(newExpanded);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    return (
        <Box className={styles.container}>
            <div style={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Box className={styles.viewHeader}>
                    <Box className={styles.titleRow}>

                        <Typography className={styles.title}>
                            <IconButton
                                onClick={() => {
                                    // setIsOpen(false);
                                    dispatch(openViewDialog(DIALOG_TYPES.EDIT, DIALOG_SUBTYPES.EDIT_PRIMARY_LIST as any));
                                }}
                            >
                                <ArrowBack style={{ color: "#1A1A1AA6" }} />
                            </IconButton>
                            Add site filter
                        </Typography>
                    </Box>
                    <Box className={styles.breadcrumb}>
                        <Typography className={`${styles.breadcrumbText} cursor-pointer`} onClick={() => {
                            dispatch(openViewDialog(DIALOG_TYPES.EDIT, DIALOG_SUBTYPES.EDIT_PRIMARY_LIST as any));
                        }}>{currentViewInfo?.clientName}</Typography>
                        <ChevronRightIcon className={styles.breadcrumbIcon} />
                        <Typography className={styles.breadcrumbText}>Sites</Typography>
                        <ChevronRightIcon className={styles.breadcrumbIcon} />
                    </Box>
                </Box>

                <Box className={styles.body}>
                   

                    <TextField
                        fullWidth
                        placeholder="Filter by keyword"
                        variant="outlined"
                        className={styles.searchField}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchLoading ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <>
                                            {searchTerm && (
                                                <IconButton
                                                    onClick={handleClearSearch}
                                                    edge="end"
                                                    sx={{ cursor: 'pointer', padding: '4px', marginRight: '8px' }}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            )}
                                            <SearchIcon />
                                        </>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />

                    {isSearching && (
                        <Typography variant="body2" className="mb-2" style={{ color: '#666' }}>
                            {displaySitesWithAccess.length + displaySitesWithoutAccess.length } search results
                        </Typography>
                    )}

                    <Box className={styles.accordionContainer}>
                        {/* Sites with Access Accordion */}
                        <Accordion
                            expanded={expandedAccordions.has('withAccess')}
                            onChange={() => handleAccordionToggle('withAccess')}
                            className={styles.accordion}
                            sx={{
                                marginBottom: 0,
                                '&.Mui-expanded': {
                                    marginBottom: 0
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                className={styles.accordionSummary}
                            >
                                <Typography className={styles.accordionTitle}>
                                    Sites with access ({displaySitesWithAccess.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails className={styles.accordionDetails}>
                                <List className={styles.list}>
                                    {displaySitesWithAccess.length === 0 && isSearching ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '20px', gap: 2 }}>
                                            <Typography variant="body2" color="textSecondary">
                                                {searchLoading ? "Loading..." : "No results."}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        displaySitesWithAccess.map((item, index) => {
                                            const isAdded = addedItems.has(item.site);
                                            const isHovered = hoveredItem === item.site;

                                            return (
                                                <Box key={item.site} >
                                                    <ListItem
                                                        className={styles.listItem}
                                                        onMouseEnter={() => setHoveredItem(item.site)}
                                                        onMouseLeave={() => setHoveredItem(null)}
                                                        onClick={() => handleToggleItem(item.site)}
                                                    >
                                                        <Box className={styles.itemContent}>
                                                            <Box className={styles.textContainer}>
                                                                <Typography className={styles.itemTitle}>
                                                                    {item.siteName}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        {isAdded ? (
                                                            <RemoveCircleOutlineIcon className={styles.editIcon} />
                                                        ) : (
                                                            isHovered && (
                                                                <AddCircleIcon className={styles.editIcon} />
                                                            )
                                                        )}
                                                    </ListItem>
                                                    {index < displaySitesWithAccess.length - 1 && (
                                                        <Divider className={styles.divider} />
                                                    )}
                                                </Box>
                                            );
                                        }))
                                    }
                                </List>
                            </AccordionDetails>
                        </Accordion>

                        {/* Sites without Access Accordion */}
                        <Accordion
                            expanded={expandedAccordions.has('withoutAccess')}
                            onChange={() => handleAccordionToggle('withoutAccess')}
                            className={styles.accordion}
                            sx={{
                                marginTop: 0,
                                '&.Mui-expanded': {
                                    marginTop: 0
                                }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                className={styles.accordionSummary}
                            >
                                <Typography className={styles.accordionTitle}>
                                    Sites without access ({displaySitesWithoutAccess.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails className={styles.accordionDetails}>
                                <List className={`${styles.list} ${styles.noAccessList}`}>
                                    {displaySitesWithoutAccess.length === 0 && isSearching ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '20px', gap: 2 }}>
                                            <Typography variant="body2" color="textSecondary">
                                                {searchLoading ? "Loading..." : "No results."}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        displaySitesWithoutAccess.map((item, index) => {
                                            const isPending = item.accessStatus === 'PENDING';
                                            const isRequestDeclined = item.accessStatus === 'COMPLETED' && item.accessLevel === 'NO_ACCESS';
                                            return (
                                                <Box key={item.site} sx={{ pr: 1 }}>
                                                    <ListItem
                                                        className={styles.listItem}
                                                        onMouseEnter={() => setHoveredItem(item.site)}
                                                        onMouseLeave={() => setHoveredItem(null)}
                                                    >
                                                        <Box className={styles.itemContent}>
                                                            <Box className={styles.textContainer}>
                                                                <Typography className={styles.itemTitle}>
                                                                    {item.siteName}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        {!isPending && !isRequestDeclined &&
                                                            (
                                                                <Button
                                                                    variant="contained"
                                                                    color="light"
                                                                    size="small"
                                                                    className={styles.requestAccessButton}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleRequestAccessClick(e, item);
                                                                    }}
                                                                >
                                                                    Request Access
                                                                </Button>
                                                            )}
                                                        {isPending &&
                                                            (
                                                                <Button
                                                                    variant="text"
                                                                    color="light"
                                                                    size="small"
                                                                    className={styles.requestedText}
                                                                    disabled
                                                                >
                                                                    Requested
                                                                </Button>
                                                            )}
                                                        {isRequestDeclined &&
                                                            (
                                                                <Button
                                                                    variant="text"
                                                                    color="light"
                                                                    size="small"
                                                                    className={styles.requestDeclinedText}
                                                                    disabled
                                                                >
                                                                    Request Declined
                                                                </Button>
                                                            )}
                                                    </ListItem>
                                                    {index < displaySitesWithoutAccess.length - 1 && (
                                                        <Divider className={styles.divider} />
                                                    )}
                                                </Box>
                                            );
                                        }))
                                    }
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                     {removedFiltersPreview.length > 0 && (
                        <Alert
                            severity="warning"
                            sx={{
                                alignItems: 'flex-start',
                                backgroundColor: '#FFF4E8',
                                border: '1px solid #F2A25A',
                                color: '#1A1A1A',
                                '& .MuiAlert-message': { width: '100%' },
                                '& .MuiAlert-icon': { color: '#F28C28', mt: '2px' },
                                '& .MuiAlert-action': { display: 'none' }
                            }}
                        >
                            <Typography sx={{ fontWeight: 600, lineHeight: '24px' }}>Heads up!</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                Your changes will remove site specific filters, such as:
                            </Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={handleUndoSiteSelection}
                                    sx={{
                                        '&&': {
                                            textTransform: 'none',
                                            minWidth: 'auto',
                                            flexShrink: 0,
                                            backgroundColor: '#FFFFFF',
                                            boxShadow: 'none'
                                        },
                                        '&&:hover': {
                                            backgroundColor: '#FFFFFF !important',
                                            boxShadow: 'none'
                                        },
                                        '&&:active': {
                                            backgroundColor: '#FFFFFF !important',
                                            boxShadow: 'none'
                                        },
                                        '&&.Mui-focusVisible': {
                                            backgroundColor: '#FFFFFF !important',
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    Undo
                                </Button>
                            </Box>
                            
                            <Box>
                                {warningExamples.slice(0, 3).map(({ siteName, type, labels }) => {
                                    const remainingCount = labels.length - 2;
                                    const labelText = labels.length <= 2
                                        ? labels.join(', ')
                                        : `${labels[0]} and ${remainingCount + 1} other${remainingCount + 1 > 1 ? 's' : ''}`;
                                    return (
                                        <Typography key={siteName} variant="body2">
                                            <strong>{siteName}</strong> - <strong>{type}</strong>: {labelText}
                                        </Typography>
                                    );
                                })}
                                {warningExamples.length > 3 && (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        ... plus {warningExamples.length - 3} other filter{warningExamples.length - 3 > 1 ? 's' : ''}.
                                    </Typography>
                                )}
                            </Box>
                        </Alert>
                    )}
                </Box>
            </div>

            <Box className={styles.footer}>
                <Typography variant="body2" className='m-0'>
                    {hasPendingSiteChanges ? 'This view has unsaved changes.' : `${addedItems.size} selected`}
                </Typography>

                <div>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleClose}
                        sx={{ mr: '12px' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!hasPendingSiteChanges || (isSearching && displaySitesWithAccess.length === 0)}
                        onClick={() => {
                            const selectedSites = getSelectedSitesFromSet(addedItems);
                            
                            // Build lookup set for filtering - if no sites selected, use all sites with access
                            const selectedLookup = selectedSites.length
                                ? buildSelectedSiteLookup(selectedSites)
                                : buildSelectedSiteLookup(sitesWithAccess);
                            
                            // Filter cost centers to only keep those belonging to selected sites
                            const nextCostCenters = filteredCostCenters.filter((costCenter: any) =>
                                isFilterApplicableToSelection(costCenter, selectedLookup)
                            );

                            dispatch(setFilteredSiteList(currentView, selectedSites));
                            dispatch(setFilteredCostCenterList(currentView, nextCostCenters));

                            setRemovedFiltersPreview([]);
                            dispatch(openViewDialog(DIALOG_TYPES.EDIT, DIALOG_SUBTYPES.EDIT_PRIMARY_LIST as any));
                        }}
                    >
                        Update
                    </Button>
                </div>
            </Box>

            {/* Request Access Popover */}
            <Popover
                open={Boolean(requestAccessAnchorEl)}
                anchorEl={requestAccessAnchorEl}
                onClose={handleCancelRequestAccess}
                anchorOrigin={{
                    vertical: popoverPosition === 'above' ? 'top' : 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: popoverPosition === 'above' ? 'bottom' : 'top',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '8px',
                            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                            maxWidth: '500px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            overflow: 'visible'
                        }
                    }
                }}
            >
                <Box sx={{ 
                    position: 'relative', 
                    paddingTop: popoverPosition === 'below' ? '12px' : '0',
                    paddingBottom: popoverPosition === 'above' ? '12px' : '0'
                }}>
                    {/* Triangle */}
                    <Box
                        sx={{
                            position: 'absolute',
                            ...(popoverPosition === 'below' ? {
                                top: '0px',
                            } : {
                                bottom: '0px',
                            }),
                            left: '80%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            zIndex: 1000,
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                ...(popoverPosition === 'below' ? {
                                    top: '1px',
                                    borderBottom: '12px solid #e0e0e0',
                                } : {
                                    bottom: '1px',
                                    borderTop: '12px solid #e0e0e0',
                                }),
                                left: '-12px',
                                width: 0,
                                height: 0,
                                borderLeft: '12px solid transparent',
                                borderRight: '12px solid transparent',
                            },
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                ...(popoverPosition === 'below' ? {
                                    top: '2px',
                                    borderBottom: '11px solid #fff',
                                } : {
                                    bottom: '2px',
                                    borderTop: '11px solid #fff',
                                }),
                                left: '-11px',
                                width: 0,
                                height: 0,
                                borderLeft: '11px solid transparent',
                                borderRight: '11px solid transparent',
                            }
                        }}
                    />

                    {/* Content Box */}
                    <Box
                        sx={{
                            borderRadius: '8px',
                            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                            maxWidth: '500px',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                            border: '1px solid #e0e0e0',
                            backgroundColor: '#fff',
                            padding: '24px'
                        }}
                    >
                        <Typography sx={{ fontSize: '18px', fontWeight: 600, mb: 2 }}>
                            Site access request
                        </Typography>
                        {/* {selectedSiteForRequest && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {selectedSiteForRequest.siteName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '13px' }}>
                                    {selectedSiteForRequest.site}
                                </Typography>
                            </Box>
                        )} */}
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Click Submit request to generate an approval request. You&apos;ll be notified by email once a decision is made.
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button
                                onClick={handleCancelRequestAccess}
                                variant="outlined"
                                color="primary"
                                disabled={requestAccessLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleRequestAccess}
                                variant="contained"
                                color="primary"
                                disabled={requestAccessLoading}
                            >
                                {requestAccessLoading ? 'Submitting...' : 'Submit request'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
};

export default ViewManagementSiteList;
